import React, { lazy } from "react";
import { Navigate, Route, Routes } from "react-router";
import { RbacRoute } from "../shared/components/routing/RbacRoute";
import { AdminRoute } from "../shared/components/routing/AdminRoute";
import NotFound from "../shared/components/routing/NotFound";
import { getFirstAllowedRoute } from "../shared/utils/tabRoutes";
import CostDashboard from "../features/cost/pages/CostDashboard";
import Layout from "../shared/components/layout/Layout";
import { useAppSelector } from "@/store/hooks";
import { selectIsAdmin, selectTabPermissions } from "@/store/authSlice";
import WeeklyCostReport from "@/features/cost/pages/weekly-cost-report/WeeklyCostReport";

// ── Lazy-loaded page bundles ─────────────────────────────────────────────────
const AlertsTrends = lazy(
  () => import("../features/alerts-trend/pages/OverallAlertsCounts"),
);
const Deployments = lazy(
  () => import("../features/deployment/pages/Deployments"),
);
const ServiceHealth = lazy(
  () => import("../features/service-health/pages/ServiceHealth"),
);
const Suppression = lazy(
  () => import("../features/alerts-suppression/pages/AlertsSuppressionPage"),
);
const SecurityAlerts = lazy(
  () => import("../features/security-alerts/pages/SecurityAlerts"),
);
const EditCustomer = lazy(
  () => import("../features/admin/pages/config/EditCustomer"),
);
const AddCustomer = lazy(
  () => import("../features/admin/pages/config/AddCustomer"),
);
const WhatsNew = lazy(() => import("../features/static/pages/WhatsNew"));
const Help = lazy(() => import("../features/static/pages/Help"));
const About = lazy(() => import("../features/static/pages/About"));
const SubscribeAlerts = lazy(
  () => import("../features/admin/pages/SubscribeAlerts/SubscribeAlerts"),
);
const NotificationTracker = lazy(
  () =>
    import("../features/admin/pages/SubscribeAlerts/CustomerNotificationTracker"),
);
const Alerts = lazy(() => import("../features/alerts/pages/Alerts"));
const WebJobs = lazy(() => import("../features/admin/pages/webjobs/Webjobs"));
const AnalyticsDashboard = lazy(
  () => import("../features/cost/pages/analytics/AnalyticsDashboard"),
);
const CostReports = lazy(
  () => import("../features/cost/pages/reports/CostReport"),
);
const SummaryDashboard = lazy(
  () => import("../features/cost/pages/summary/SummaryDashboard"),
);
const AdminPage = lazy(() => import("../features/admin/pages/AdminPage"));
const UserManagement = lazy(
  () => import("../features/admin/pages/rbac/UserManagement"),
);
const RoleManagement = lazy(
  () => import("../features/admin/pages/rbac/RoleManagement"),
);
const TabManagement = lazy(
  () => import("../features/admin/pages/rbac/TabManagement"),
);
const AuditLog = lazy(() => import("../features/admin/pages/rbac/AuditLog"));

// ── Home redirect ────────────────────────────────────────────────────────────
// RBAC users land on the first permitted tab; non-RBAC / fallback → Alerts.
const HomeRedirect: React.FC = () => {
  const tabPermissions = useAppSelector(selectTabPermissions);
  const isAdmin = useAppSelector(selectIsAdmin);

  if (!tabPermissions) return <Alerts />;
  if ("ALERTS" in tabPermissions) return <Alerts />;

  const route = getFirstAllowedRoute(tabPermissions);
  if (route) return <Navigate to={route} replace />;
  if (isAdmin) return <Navigate to="/admin/config/add" replace />;

  return (
    <NotFound
      title="No access"
      message="No pages are assigned to your role yet. Please contact an administrator."
      redirectSeconds={0}
    />
  );
};

// ── Route tree ───────────────────────────────────────────────────────────────
interface AppRoutesProps {
  handleLogout: () => void;
  jwtReady: boolean;
}

export const AppRoutes: React.FC<AppRoutesProps> = ({
  handleLogout,
  jwtReady,
}) => (
  <Routes>
    <Route
      path="/"
      element={<Layout logout={handleLogout} jwtReady={jwtReady} />}
    >
      <Route index element={<HomeRedirect />} />

      <Route
        path="securityAlerts"
        element={
          <RbacRoute tabCode="SECURITY_ALERTS">
            <SecurityAlerts />
          </RbacRoute>
        }
      />

      <Route
        path="deployments"
        element={<RbacRoute tabCode="DEPLOYMENT"><Deployments /></RbacRoute>}
      />

      <Route
        path="serviceHealth"
        element={<RbacRoute tabCode={["SERVICE_HEALTH", "SERVICEHEALTH"]}><ServiceHealth /></RbacRoute>}
      />

      <Route
        path="alertsTrend"
        element={
          <RbacRoute tabCode="ALERTS_TREND">
            <AlertsTrends />
          </RbacRoute>
        }
      />

      <Route
        path="costManagement"
        element={
          <RbacRoute tabCode="COST">
              <CostDashboard />
          </RbacRoute>
        }
      >
        <Route path="dashboard" element={<SummaryDashboard />} />
        <Route path="reports" element={<CostReports />} />
        <Route path="analytics" element={<AnalyticsDashboard />} />
        <Route path="weekly-cost-report" element={<WeeklyCostReport />} />
        
      </Route>

      <Route
        path="suppression"
        element={
          <RbacRoute tabCode="ALERTS_SUPPRESSION">
            <Suppression />
          </RbacRoute>
        }
      />

      <Route path="config/edit" element={<Navigate to="/admin/config/edit" replace />} />
      <Route path="config/add" element={<Navigate to="/admin/config/add" replace />} />
      <Route path="config/subscribe" element={<Navigate to="/admin/subscribe/alerts" replace />} />
      <Route
        path="config/CustomerNotificationTracker"
        element={<Navigate to="/admin/subscribe/tracker" replace />}
      />
      <Route path="config/webJobs" element={<Navigate to="/admin/webjobs" replace />} />
      <Route
        path="config/subscribe/:customer/:usecase/:product/:alertName"
        element={<Navigate to="/admin/subscribe/alerts" replace />}
      />

      <Route
        path="admin/*"
        element={
          <AdminRoute>
            <AdminPage />
          </AdminRoute>
        }
      >
        <Route index element={<Navigate to="config/add" replace />} />
        <Route path="config/edit" element={<EditCustomer />} />
        <Route path="config/add" element={<AddCustomer />} />
        <Route path="subscribe/alerts" element={<SubscribeAlerts />} />
        <Route path="subscribe/tracker" element={<NotificationTracker />} />
        <Route path="webjobs" element={<WebJobs />} />
        <Route
          path="subscribe/alerts/:customer/:usecase/:product/:alertName"
          element={<SubscribeAlerts />}
        />
        <Route path="rbac/users" element={<UserManagement />} />
        <Route path="rbac/roles" element={<RoleManagement />} />
        <Route path="rbac/tabs" element={<TabManagement />} />
        <Route path="rbac/audit-logs" element={<AuditLog />} />
      </Route>
    </Route>

    <Route path="/whats-new" element={<WhatsNew />} />
    <Route path="/help" element={<Help />} />
    <Route path="/about" element={<About />} />

    <Route path="*" element={<NotFound />} />
  </Routes>
);
